import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Table, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, ButtonGroup, Col } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import encode from "nodejs-base64-encode";
import axios from "axios";
import moment from "moment";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import { abrirPDF } from "../../../../utils/abrirPDF";

// Antigüedad de saldos (igual a AmericanFly): por cliente, el saldo repartido por días de
// vencimiento (fecha de la nota + plazo del cliente). Cada monto abre el detalle de las notas.
const URL_ESTADO_CUENTA = process.env.REACT_APP_URL_ESTADO_CUENTA;
const URL_SEND_MAIL_ATT = process.env.REACT_APP_URL_SEND_MAIL_ATT;

const RANGOS = [
  { campo: "enTiempo", titulo: "En Tiempo", corto: "En Tiempo" },
  { campo: "treinta", titulo: "Vencido 30", corto: "30" },
  { campo: "sesenta", titulo: "Vencido 60", corto: "60" },
  { campo: "noventa", titulo: "Vencido 90", corto: "90" },
  { campo: "cientoveinte", titulo: "Vencido 120", corto: "120" },
  { campo: "cientoochenta", titulo: "Vencido 180", corto: "180" },
  { campo: "otro", titulo: "Más de 180", corto: "+ 180" },
];
const NUMERICOS = ["saldo", ...RANGOS.map((r) => r.campo)];
const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
const pct = (n) => new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 2 }).format(n || 0);
const ITEMS_PER_PAGE = 50;

function AntiguedadSaldos() {
  const { user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const [modalMail, setModalMail] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [detalle, setDetalle] = useState(null); // { cliente, rango, notas }

  useEffect(() => {
    axios
      .get(`${URL_ESTADO_CUENTA}AntiguedadSaldos`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => setComments((r.data || []).sort((a, b) => (a.nombreCliente > b.nombreCliente ? 1 : -1))))
      .catch((err) => console.log(err));
  }, []);

  // Búsqueda (cliente o vendedor) — la usan la tabla, el PDF, el Excel y el mail
  const filtrados = useMemo(() => {
    const q = search.toLowerCase();
    return comments.filter(
      (c) => !q || (c.nombreCliente || "").toLowerCase().includes(q) || (c.vendedor || "").toLowerCase().includes(q)
    );
  }, [comments, search]);

  const totales = useMemo(() => {
    const t = {};
    NUMERICOS.forEach((k) => (t[k] = filtrados.reduce((s, c) => s + (c[k] || 0), 0)));
    return t;
  }, [filtrados]);

  const commentsData = useMemo(() => {
    let lista = [...filtrados];
    setTotalItems(lista.length);
    if (sorting.field) {
      const rev = sorting.order === "asc" ? 1 : -1;
      lista.sort((a, b) =>
        NUMERICOS.includes(sorting.field)
          ? rev * ((a[sorting.field] || 0) - (b[sorting.field] || 0))
          : rev * String(a[sorting.field] || "").localeCompare(String(b[sorting.field] || ""))
      );
    }
    return lista.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [filtrados, currentPage, sorting]);

  const headers = [
    { name: "Cliente", field: "nombreCliente", sortable: true },
    { name: "Vendedor", field: "vendedor", sortable: true },
    { name: "Saldo Total", field: "saldo", sortable: true },
    ...RANGOS.map((r) => ({ name: r.titulo, field: r.campo, sortable: true })),
  ];

  function abrirDetalle(c, rango) {
    setDetalle({ cliente: c.nombreCliente, plazo: c.plazo, rango, notas: c.detalle.filter((n) => n.rango === rango.campo) });
  }

  function documentoPDF() {
    const doc = new jsPDF("landscape");
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 140, 15, 25, 25);
    doc.text(`Antiguedad de Saldos`, 20, 30);
    doc.text(`Al ${moment().format("DD/MM/YYYY")}`, 20, 40);
    doc.autoTable({
      head: [["Cliente", "Vendedor", "Saldo", ...RANGOS.map((r) => r.corto)]],
      body: filtrados.map((c) => [c.nombreCliente, c.vendedor, fmt(c.saldo), ...RANGOS.map((r) => fmt(c[r.campo]))]),
      foot: [
        ["Total", "", fmt(totales.saldo), ...RANGOS.map((r) => fmt(totales[r.campo]))],
        ["Porcentaje", "", pct(1), ...RANGOS.map((r) => pct(totales.saldo ? totales[r.campo] / totales.saldo : 0))],
      ],
      startY: 45,
      theme: "grid",
      styles: { fontSize: 8 },
      columnStyles: Object.fromEntries([2, 3, 4, 5, 6, 7, 8, 9].map((i) => [i, { halign: "right" }])),
      showFoot: "lastPage",
    });
    return doc;
  }

  function PDFTabla() {
    abrirPDF(documentoPDF());
  }

  function excel() {
    const cols = ["Cliente", "Vendedor", "Saldo", ...RANGOS.map((r) => r.corto)];
    const datos = filtrados.map((c) => {
      const o = { Cliente: c.nombreCliente, Vendedor: c.vendedor, Saldo: c.saldo };
      RANGOS.forEach((r) => (o[r.corto] = c[r.campo]));
      return o;
    });
    new ExportJsonExcel({
      fileName: "AntiguedadSaldos",
      datas: [{ sheetData: datos, sheetName: "AntiguedadSaldos", sheetFilter: cols, sheetHeader: cols }],
    }).saveExcel();
  }

  function enviaMail() {
    const base64File = encode.encode(documentoPDF().output("arraybuffer"), "base64");
    axios
      .post(
        URL_SEND_MAIL_ATT,
        {
          subject: "Antiguedad Saldos",
          email: mailTo,
          fileName: "AntiguedadSaldos.pdf",
          att: base64File,
          text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
         <th><img width="${process.env.REACT_APP_TAMANO_LOGO_MAIL}" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}logoInstitucion.png" /></th>
         <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${process.env.REACT_APP_NOMBRE_INSTITUCION}</tr>
         <tbody>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Adjunto encontraras el listado de Antiguedad de Saldos.</tr>
         </tbody>
         <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda favor de comunicarse al ${process.env.REACT_APP_TELEFONO_INSTITUCION}</tr>
         </table>`,
        },
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      )
      .then(() => {
        setModalMail(false);
        setMailTo("");
        Swal.fire({ position: "center", icon: "success", title: "Enviado con Exito!", showConfirmButton: false, timer: 2000 });
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Oops...", text: "No se pudo enviar el correo" });
      });
  }

  function PDFDetalle() {
    const doc = new jsPDF("landscape");
    doc.text(`Antiguedad de Saldos · ${detalle.cliente}`, 14, 16);
    doc.setFontSize(11);
    doc.text(`${detalle.rango.titulo} · Plazo ${detalle.plazo} días · Al ${moment().format("DD/MM/YYYY")}`, 14, 23);
    doc.autoTable({
      head: [["Tipo", "Número", "Artículo", "Fecha", "Fecha Venc.", "Días Venc.", "Total", "Saldo"]],
      body: detalle.notas.map((n) => [
        n.tipo,
        n.numero,
        `${n.articulo}${n.masArticulos ? ` (+${n.masArticulos})` : ""}`,
        n.fecha,
        n.fechaVencimiento,
        n.diasVencido || "",
        fmt(n.total),
        fmt(n.saldo),
      ]),
      foot: [["Total", "", "", "", "", "", "", fmt(detalle.notas.reduce((t, n) => t + n.saldo, 0))]],
      startY: 28,
      theme: "grid",
      styles: { fontSize: 8 },
      columnStyles: { 5: { halign: "center" }, 6: { halign: "right" }, 7: { halign: "right" } },
    });
    abrirPDF(doc);
  }

  if (!user.menu_admin) return <Header />;

  const totalPagina = {};
  NUMERICOS.forEach((k) => (totalPagina[k] = commentsData.reduce((s, c) => s + (c[k] || 0), 0)));

  return (
    <>
      <Header />
      <br />
      <br />
      <div className="card col-12">
        <Row>
          <Col md={8}>
            <Button size="sm" href="/MenuAdmin" className="btn btn-danger" id="botonListado">
              Regresar
            </Button>
          </Col>
          <Col sm={4}>
            <ButtonGroup id="logoutBoton">
              <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                PDF <i className="far fa-file-pdf"></i>
              </Button>
              <Button size="sm" className="btn" onClick={() => setModalMail(true)} color="info">
                eMail <i className="fas fa-at"></i>
              </Button>
              <Button size="sm" className="btn" color="primary" onClick={excel}>
                Excel <i className="far fa-file-excel"></i>
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        <h3 align="center">Antigüedad de Saldos</h3>
        <p align="center" style={{ color: "#64748b", marginTop: -6 }}>
          Días de vencimiento según el plazo de cada cliente. Toca un monto para ver las notas.
        </p>
        <div className="row">
          <div className="col-md-8">
            <Pagination total={totalItems} itemsPerPage={ITEMS_PER_PAGE} currentPage={currentPage} onPageChange={(page) => setCurrentPage(page)} />
          </div>
          <div className="col-md-4 d-flex flex-row-reverse">
            <Search
              onSearch={(value) => {
                setSearch(value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
        <br />
        <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
          <TableHeader headers={headers} onSorting={(field, order) => setSorting({ field, order })} />
          <tbody>
            {commentsData.map((c) => (
              <tr key={c.clienteId}>
                <td>{c.nombreCliente}</td>
                <td>{c.vendedor}</td>
                <td className="negrita">{fmt(c.saldo)}</td>
                {RANGOS.map((r) => (
                  <td key={r.campo}>
                    {Math.abs(c[r.campo]) > 0.1 ? (
                      <Button
                        size="sm"
                        className="btn"
                        color={r.campo === "enTiempo" ? "info" : "danger"}
                        title={`Ver notas: ${r.titulo}`}
                        onClick={() => abrirDetalle(c, r)}
                      >
                        {fmt(c[r.campo])}
                      </Button>
                    ) : undefined}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="negrita" align="center">
                TOTAL
              </td>
              <td></td>
              <td className="negrita">{fmt(totalPagina.saldo)}</td>
              {RANGOS.map((r) => (
                <td key={r.campo} className="negrita">
                  {fmt(totalPagina[r.campo])}
                </td>
              ))}
            </tr>
            <tr>
              <td className="negrita" align="center">
                TOTAL GENERAL
              </td>
              <td></td>
              <td className="negrita">{fmt(totales.saldo)}</td>
              {RANGOS.map((r) => (
                <td key={r.campo} className="negrita">
                  {fmt(totales[r.campo])}
                </td>
              ))}
            </tr>
            <tr>
              <td className="negrita" align="center">
                PORCENTAJE
              </td>
              <td></td>
              <td className="negrita">{pct(totales.saldo ? 1 : 0)}</td>
              {RANGOS.map((r) => (
                <td key={r.campo} className="negrita">
                  {pct(totales.saldo ? totales[r.campo] / totales.saldo : 0)}
                </td>
              ))}
            </tr>
          </tbody>
        </Table>
        <div className="col-md-6">
          <Pagination total={totalItems} itemsPerPage={ITEMS_PER_PAGE} currentPage={currentPage} onPageChange={(page) => setCurrentPage(page)} />
        </div>
      </div>
      <br />

      <Modal size="sm" isOpen={modalMail} toggle={() => setModalMail(false)}>
        <ModalHeader toggle={() => setModalMail(false)}>
          <h4>Enviar Antigüedad Saldos</h4>
        </ModalHeader>
        <ModalBody>
          <Label className="mr-sm-2">Email</Label>
          <Input className="col-sm-12" type="text" value={mailTo} required onChange={(e) => setMailTo(e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button size="sm" color="success" onClick={enviaMail}>
            Enviar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal size="xl" isOpen={!!detalle} toggle={() => setDetalle(null)}>
        {detalle ? (
          <>
            <ModalHeader toggle={() => setDetalle(null)}>
              <h4>
                {detalle.cliente} · {detalle.rango.titulo}
              </h4>
              <div style={{ fontSize: 13, color: "#64748b" }}>Plazo del cliente: {detalle.plazo} días</div>
            </ModalHeader>
            <ModalBody>
              <Table size="sm" striped>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Número</th>
                    <th>Artículo</th>
                    <th>Vendedor</th>
                    <th>Fecha</th>
                    <th>Fecha Venc.</th>
                    <th>Días Venc.</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                    <th style={{ textAlign: "right" }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.notas.map((n) => (
                    <tr key={`${n.tipo}-${n.numero}`}>
                      <td>{n.tipo}</td>
                      <td>
                        {n.numero}
                        {n.factura ? <div style={{ fontSize: 11, color: "#64748b" }}>Fact. {n.factura}</div> : null}
                      </td>
                      <td>
                        {n.articulo}
                        {n.masArticulos ? <span style={{ color: "#64748b" }}> (+{n.masArticulos} más)</span> : null}
                      </td>
                      <td>{n.vendedores}</td>
                      <td>{n.fecha}</td>
                      <td>{n.fechaVencimiento}</td>
                      <td style={{ color: n.diasVencido ? "#b91c1c" : undefined, fontWeight: n.diasVencido ? 700 : 400 }}>
                        {n.diasVencido || ""}
                      </td>
                      <td style={{ textAlign: "right" }}>{fmt(n.total)}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(n.saldo)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={8} className="negrita" style={{ textAlign: "right" }}>
                      Total {detalle.notas.length} nota(s)
                    </td>
                    <td className="negrita" style={{ textAlign: "right" }}>
                      {fmt(detalle.notas.reduce((t, n) => t + n.saldo, 0))}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </ModalBody>
            <ModalFooter>
              <Button size="sm" color="danger" onClick={PDFDetalle}>
                PDF <i className="far fa-file-pdf"></i>
              </Button>
              <Button size="sm" color="success" onClick={() => setDetalle(null)}>
                Cerrar
              </Button>
            </ModalFooter>
          </>
        ) : null}
      </Modal>
    </>
  );
}

export default AntiguedadSaldos;
